import React, { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../components/ui/table";
import Badge from "../../../components/ui/badge/Badge";
import Button from "../../../components/ui/button/Button";
import type { Factura } from "../types/index";
import ComponentCard from "../../../components/common/ComponentCard";
import { Eye, FileText, Trash2, Plus, Search, Filter, X, Download, FileCheck, Calendar, User, DollarSign, Edit2, Tag } from 'lucide-react';
import Select from "../../../components/form/Select";
import Input from "../../../components/form/input/InputField";
import { FacturaForm } from "./FacturaForm";
import { Modal } from "../../../components/ui/modal";
import useApi from '../../../hooks/useApi';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Formik, Form, Field } from 'formik';
import useAuth from "../../../hooks/useAuth";
import RoleBasedRenderer from "../../../components/auth/RoleBasedRenderer";
import { generateFacturaPDF } from './generateFacturaPDF';

interface FacturaListProps {
  facturas: Factura[];
  onSearch: Dispatch<SetStateAction<string>>;
  onFacturaDeleted?: () => Promise<void>;
  onDataChange?: () => Promise<void>;
}

interface Filters {
  estado: string;
  fechaInicio: string;
  fechaFin: string;
  cliente: string;
  montoMin: string;
  montoMax: string;
  soloManuales: boolean;
}

// Add type adapter
const formatDate = (date: string | Date): string => {
  return format(new Date(date), 'dd/MM/yyyy');
};

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const adaptFactura = (apiFactura: any): Factura => ({
  id: apiFactura.id,
  numero: apiFactura.numero,
  titulo: apiFactura.titulo,
  fecha: apiFactura.fecha,
  clientId: apiFactura.clientId,
  clientType: apiFactura.clientType,
  estado: apiFactura.estado,
  lineas: apiFactura.lineas || [],
  subtotal: apiFactura.subtotal,
  impuestoTotal: apiFactura.impuestoTotal,
  total: apiFactura.total,
  abono: apiFactura.abono,
  saldoPendiente: apiFactura.saldoPendiente,
  notas: apiFactura.notas,
  entregadoPor: apiFactura.entregadoPor,
  recibidoPor: apiFactura.recibidoPor,
  clienteNombre: apiFactura.clienteNombre,
  manualInvoice: apiFactura.manualInvoice,
  clienteNombreManual: apiFactura.clienteNombreManual,
  reservationId: apiFactura.reservationId,
  reservation: apiFactura.reservation ? {
    id: apiFactura.reservation.id,
    descuento: apiFactura.reservation.descuento || 0,
    taxRate: apiFactura.reservation.taxRate || 18,
    type: apiFactura.reservation.type || undefined,
    agenciaComision: apiFactura.reservation.agenciaComision || 0
  } : null
});

const FacturaList = ({ facturas, onSearch, onFacturaDeleted, onDataChange }: FacturaListProps): JSX.Element => {
  const [facturasState, setFacturas] = useState<Factura[]>([]);
  const [selectedFactura, setSelectedFactura] = useState<Factura | null>(null);
  const [editingFactura, setEditingFactura] = useState<Factura | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [hotels, setHotels] = useState<string[]>([]);
  const [filters, setFilters] = useState<Filters>({
    estado: '',
    fechaInicio: '',
    fechaFin: '',
    cliente: '',
    montoMin: '',
    montoMax: '',
    soloManuales: false
  });
  const api = useApi();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [facturaIdToDelete, setFacturaIdToDelete] = useState<number | null>(null);
  const [facturaNotas, setFacturaNotas] = useState<any[]>([]);
  const { canDelete, canEdit } = useAuth();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Add pagination state variables after the other state declarations
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // Number of facturas to show per page

  // Add useEffect to reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Fetch facturas on component mount
  useEffect(() => {
    loadFacturas();
    loadHotels();
  }, []);

  const loadHotels = async (): Promise<void> => {
    try {
      const hotelData = await api.getHotels();
      setHotels(hotelData || []);
    } catch (error) {
      toast.error('Error loading hotels');
    }
  };

  const loadFacturas = async (): Promise<void> => {
    try {
      // Obtener los datos actualizados silenciosamente
      const data = await api.getFacturas();
      
      // Adaptar y actualizar el estado
      const facturasActualizadas = data.map(adaptFactura);
      setFacturas(facturasActualizadas);
      
      // Notificar al componente padre que los datos han cambiado (si es necesario)
      if (onDataChange) {
        await onDataChange();
      }
    } catch (error) {
      toast.error('Error al cargar las facturas');
    }
  };

  const handleOpenForm = () => {
    setSelectedFactura(null);
    setIsFormOpen(true);
  };

  const handleViewDetails = async (id: number | undefined) => {
    if (!id) {
      toast.error('ID de factura inválido');
      return;
    }
    
    try {
      const factura = await api.getFactura(id);
      
      // Asegurarse de que la comisión de agencia esté incluida en los datos de la reserva
      if (factura.reservation && factura.reservation.type === 'b2b') {
        // Si no existe agenciaComision en la reserva pero hay un reservationId
        if (factura.reservation && !('agenciaComision' in factura.reservation) && factura.reservationId) {
          try {
            // Intentar obtener la reserva completa para asegurar que tenemos todos los datos
            const reservationData = await api.getReservation(factura.reservationId);
            if (reservationData && 'agenciaComision' in reservationData) {
              // Asegurémonos de que reservation existe y tiene la propiedad agenciaComision
              if (factura.reservation) {
                factura.reservation.agenciaComision = reservationData.agenciaComision;
              }
            }
          } catch (error) {
            console.error("Error al obtener datos adicionales de la reserva:", error);
          }
        }
      }
      
      setSelectedFactura(factura);
      setIsDetailsModalOpen(true);
      // Fetch associated notas
      await fetchFacturaNotas(id);
    } catch (error) {
      console.error("Error fetching factura details:", error);
      toast.error("No se pudo obtener detalles de la factura");
    }
  };

  const handleExportFactura = async (id: number | undefined) => {
    if (!id) {
      toast.error('ID de factura inválido');
      return;
    }
    
    try {
      const factura: Factura = await api.getFactura(id);
      
      // Asegurarse de que la comisión de agencia esté incluida en los datos de la reserva
      if (factura.reservation && factura.reservation.type === 'b2b') {
        // Si no existe agenciaComision en la reserva pero hay un reservationId
        if (factura.reservation && !('agenciaComision' in factura.reservation) && factura.reservationId) {
          try {
            // Intentar obtener la reserva completa para asegurar que tenemos todos los datos
            const reservationData = await api.getReservation(factura.reservationId);
            if (reservationData && 'agenciaComision' in reservationData) {
              // Asegurémonos de que reservation existe y tiene la propiedad agenciaComision
              if (factura.reservation) {
                factura.reservation.agenciaComision = reservationData.agenciaComision;
              }
            }
          } catch (error) {
            console.error("Error al obtener datos adicionales de la reserva:", error);
          }
        }
      }
      
      // Get notas for this factura
      const notas = await api.getNotasByFactura(id);
      
      await generateFacturaPDF(
        factura,
        notas,
        () => toast.success('Factura exportada exitosamente'),
        (error) => {
          console.error("Error generando PDF:", error);
          toast.error("No se pudo generar el PDF");
        }
      );
    } catch (error) {
      console.error("Error fetching factura details:", error);
      toast.error("No se pudo obtener detalles de la factura");
    }
  };

  const handleDeleteFactura = async (id: number | undefined) => {
    if (!id) {
      toast.error('ID de factura inválido');
      return;
    }
    setIsDeleteModalOpen(true);
    setFacturaIdToDelete(id);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedFactura(null);
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedFactura(null);
  };

  const handleSubmitForm = (data: any) => {
    // TODO: Implementar creación de factura con API
    setIsFormOpen(false);
    
    // Para compatibilidad con la implementación anterior
    if (onFacturaDeleted) {
      onFacturaDeleted();
    }
    
    // Notificar más genéricamente sobre cambios en los datos
    if (onDataChange) {
      onDataChange();
    }
  };

  const handleEditFactura = async (id: number | undefined) => {
    if (!id) {
      toast.error('ID de factura inválido');
      return;
    }
    
    try {
      const factura = await api.getFactura(id);
      setEditingFactura(adaptFactura(factura));
      setIsEditModalOpen(true);
    } catch (error) {
      toast.error('Error al cargar los detalles de la factura para editar');
    }
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingFactura(null);
  };

  const handleSubmitEditForm = async (data: any, helpers: any) => {
    if (!editingFactura?.id) {
      toast.error('ID de factura inválido');
      return;
    }
    
    try {
      // Update factura with API
      await api.updateFactura(editingFactura.id, data);
      toast.success('Factura actualizada exitosamente');
      
      // Reset form and close modal
      helpers.resetForm();
      setIsEditModalOpen(false);
      setEditingFactura(null);
      
      // Refresh data
      loadFacturas();
    } catch (error) {
      toast.error('Error al actualizar la factura');
    }
  };

  const handleFilterChange = (key: keyof Filters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      estado: '',
      fechaInicio: '',
      fechaFin: '',
      cliente: '',
      montoMin: '',
      montoMax: '',
      soloManuales: false
    });
  };

  // Replace the modified filteredFacturas function with this corrected version that respects the original filtering logic
  const filteredFacturas = facturas.filter(factura => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      factura.numero?.toLowerCase().includes(searchLower) ||
      factura.clienteNombre?.toLowerCase().includes(searchLower) ||
      factura.total?.toString().includes(searchLower);
    
    if (!matchesSearch) return false;
    
    // Other filters
    if (filters.estado && factura.estado !== filters.estado) return false;
    if (filters.cliente && (!factura.clienteNombre || !factura.clienteNombre.toLowerCase().includes(filters.cliente.toLowerCase()))) return false;
    if (filters.fechaInicio && new Date(factura.fecha) < new Date(filters.fechaInicio)) return false;
    if (filters.fechaFin && new Date(factura.fecha) > new Date(filters.fechaFin)) return false;
    if (filters.montoMin && factura.total < Number(filters.montoMin)) return false;
    if (filters.montoMax && factura.total > Number(filters.montoMax)) return false;
    if (filters.soloManuales && !factura.manualInvoice) return false;
    return true;
  });

  // Calculate pagination values
  const totalPages = Math.ceil(filteredFacturas.length / itemsPerPage);
  const paginatedFacturas = filteredFacturas.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Add pagination control functions
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Add a function to fetch notas for a factura
  const fetchFacturaNotas = async (facturaId: number) => {
    try {
      const notas = await api.getNotasByFactura(facturaId);
      setFacturaNotas(notas);
      return notas;
    } catch (error) {
      console.error("Error fetching notas for factura:", error);
      toast.error("No se pudieron cargar las notas asociadas a esta factura");
      return [];
    }
  };

  return (
    <ComponentCard title="Facturas" className="h-full">
      <div className="p-2 space-y-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Buscar facturas..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                onSearch(e.target.value);
              }}
              className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2.5 text-sm text-gray-900 dark:text-white dark:border-gray-600 dark:bg-gray-800 dark:placeholder-gray-400 focus:ring-primary-500 focus:border-primary-500"
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400 dark:text-gray-500" />
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              startIcon={<Filter className="w-4 h-4" />}
            >
              Filtros
            </Button>
            <RoleBasedRenderer requiredPermission="create">
              <Button
                variant="primary"
                size="sm"
                startIcon={<Plus className="w-4 h-4" />}
                onClick={handleOpenForm}
              >
                Nueva factura
              </Button>
            </RoleBasedRenderer>
          </div>
        </div>

        {showFilters && (
          <div className="p-5 bg-gradient-to-br from-gray-50 to-white dark:from-gray-800/70 dark:to-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 pb-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Filter className="w-4 h-4 text-primary-500" />
                  Filtros avanzados
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Filtra las facturas según los criterios que necesites
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                startIcon={<X className="w-4 h-4" />}
                className="self-start sm:self-center"
              >
                Limpiar filtros
              </Button>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-4">
              <div className="space-y-1.5">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <FileCheck className="w-4 h-4 text-primary-500 mr-1.5" />
                  Estado
                </label>
                <Select
                  options={[
                    { value: 'pendiente', label: 'Pendiente de pago' },
                    { value: 'pagada', label: 'Pagada' },
                    { value: 'anulada', label: 'Anulada' }
                  ]}
                  defaultValue={filters.estado}
                  onChange={(value) => handleFilterChange('estado', value)}
                  placeholder="Seleccionar estado"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 text-primary-500 mr-1.5" />
                  Desde
                </label>
                <Input
                  type="date"
                  value={filters.fechaInicio}
                  onChange={(e) => handleFilterChange('fechaInicio', e.target.value)}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <Calendar className="w-4 h-4 text-primary-500 mr-1.5" />
                  Hasta
                </label>
                <Input
                  type="date"
                  value={filters.fechaFin}
                  onChange={(e) => handleFilterChange('fechaFin', e.target.value)}
                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <User className="w-4 h-4 text-primary-500 mr-1.5" />
                  Cliente
                </label>
                <Input
                  type="text"
                  value={filters.cliente}
                  onChange={(e) => handleFilterChange('cliente', e.target.value)}
                  placeholder="Nombre del cliente"
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <DollarSign className="w-4 h-4 text-primary-500 mr-1.5" />
                  Importe mínimo
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    value={filters.montoMin}
                    onChange={(e) => handleFilterChange('montoMin', e.target.value)}
                    placeholder="0.00"
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="space-y-1.5">
                <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  <DollarSign className="w-4 h-4 text-primary-500 mr-1.5" />
                  Importe máximo
                </label>
                <div className="relative">
                  <Input
                    type="number"
                    value={filters.montoMax}
                    onChange={(e) => handleFilterChange('montoMax', e.target.value)}
                    placeholder="0.00"
                    className="w-full"
                  />
                </div>
              </div>
              
              <div className="space-y-1.5 flex items-center">
                <div className="bg-gray-50 dark:bg-gray-800 p-2.5 rounded-lg border border-gray-300 dark:border-gray-600 flex items-center w-full h-[42px] mt-6">
                  <input
                    type="checkbox"
                    id="facturasManuales"
                    checked={filters.soloManuales}
                    onChange={(e) => handleFilterChange('soloManuales', e.target.checked)}
                    className="h-4 w-4 text-primary-600 rounded focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700"
                  />
                  <label htmlFor="facturasManuales" className="ml-2 text-sm text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                    <FileText className="w-4 h-4 text-primary-500" />
                    Facturas manuales
                  </label>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent dark:scrollbar-track-gray-800/50 overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <Table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
              <TableRow>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Número
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Cliente
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Fecha
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Total
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Estado
                </TableCell>
                <TableCell isHeader className="px-6 py-3.5 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Acciones
                </TableCell>
              </TableRow>
            </TableHeader>
            <TableBody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
              {filteredFacturas.length > 0 ? (
                paginatedFacturas.map((factura) => (
                  <TableRow 
                    key={factura.id} 
                    className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <TableCell 
                      className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap"
                    >
                      {factura.numero}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">
                      <div className="flex items-center space-x-2">
                        {/* Manual/Auto Badge */}
                        {factura.manualInvoice ? (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded">
                            MANUAL
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded">
                            AUTO
                          </span>
                        )}

                        {/* B2B/B2C Badge removed as requested */}
                        <span className="truncate max-w-[150px]" title={factura.clienteNombre}>
                          {factura.clienteNombre}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 whitespace-nowrap">
                      {format(parseISO(factura.fecha), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 text-right whitespace-nowrap font-medium">
                      ${factura.total !== undefined && factura.total !== null ? factura.total.toFixed(2) : '0.00'}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        factura.estado === 'pendiente'
                          ? 'bg-yellow-100 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-400 dark:border-yellow-800'
                          : factura.estado === 'pagada'
                          ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-400 dark:border-green-800'
                          : 'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-400 dark:border-red-800'
                      }`}>
                        {factura.estado === 'pendiente' ? 'Pendiente' : 
                         factura.estado === 'pagada' ? 'Pagada' : 'Anulada'}
                      </span>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-sm">
                      <div className="flex space-x-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => factura.id && handleViewDetails(factura.id)}
                          aria-label="Ver detalles"
                          className="text-gray-500 hover:text-gray-700 hover:border-gray-500"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => factura.id && handleExportFactura(factura.id)}
                          aria-label="Exportar factura"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        {!factura.reservationId && (
                          <RoleBasedRenderer requiredPermission="edit">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => factura.id && handleEditFactura(factura.id)}
                              aria-label="Editar factura"
                              className="text-blue-500 hover:text-blue-700 hover:border-blue-500"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          </RoleBasedRenderer>
                        )}
                        {(!factura.reservationId || factura.manualInvoice) ? (
                          <RoleBasedRenderer requiredPermission="delete">
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => factura.id && handleDeleteFactura(factura.id)}
                              aria-label="Eliminar factura"
                              className="text-red-500 hover:text-red-700 hover:border-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </RoleBasedRenderer>
                        ) : (
                          <div title="No se pueden eliminar facturas automáticas. Para modificarla, edite la reserva correspondiente.">
                            <Button 
                              variant="outline" 
                              size="sm"
                              disabled
                              aria-label="No se puede eliminar"
                              className="text-gray-400 cursor-not-allowed"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">
                    No se encontraron facturas.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Add pagination controls after the table but before modals */}
        {filteredFacturas.length > itemsPerPage && (
          <div className="flex items-center justify-between px-4 py-3 sm:px-6 border-t border-gray-200 dark:border-gray-700 mt-4">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className={`relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md 
                  ${currentPage === 1 
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                  border border-gray-300 dark:border-gray-600`}
              >
                Anterior
              </button>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className={`ml-3 relative inline-flex items-center px-4 py-2 text-sm font-medium rounded-md
                  ${currentPage === totalPages 
                    ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'} 
                  border border-gray-300 dark:border-gray-600`}
              >
                Siguiente
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Mostrando <span className="font-medium">{Math.min((currentPage - 1) * itemsPerPage + 1, filteredFacturas.length)}</span> a{" "}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredFacturas.length)}</span> de{" "}
                  <span className="font-medium">{filteredFacturas.length}</span> facturas
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={handlePrevPage}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                      ${currentPage === 1 
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    <span className="sr-only">Anterior</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                  
                  {/* Page numbers - limited to 5 pages */}
                  {(() => {
                    // Calculate which page numbers to show (max 5)
                    let startPage = Math.max(1, currentPage - 2);
                    let endPage = Math.min(totalPages, startPage + 4);
                    
                    // Adjust startPage if we're near the end
                    if (endPage - startPage < 4 && startPage > 1) {
                      startPage = Math.max(1, endPage - 4);
                    }
                    
                    const pages = [];
                    for (let i = startPage; i <= endPage; i++) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium
                            ${currentPage === i
                              ? 'z-10 bg-indigo-50 dark:bg-indigo-900/30 border-indigo-500 dark:border-indigo-500 text-indigo-600 dark:text-indigo-400'
                              : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                        >
                          {i}
                        </button>
                      );
                    }
                    return pages;
                  })()}
                  
                  <button
                    onClick={handleNextPage}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 text-sm font-medium
                      ${currentPage === totalPages 
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed' 
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                  >
                    <span className="sr-only">Siguiente</span>
                    <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Form for creating new invoices */}
      <FacturaForm
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSuccess={loadFacturas}
        onCancel={handleCloseForm}
        onSubmit={handleSubmitForm}
        factura={selectedFactura || undefined}
      />

      {/* Custom Modal for editing manual invoices */}
      {editingFactura && (
        <Modal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          title="Editar factura"
          size="md"
        >
          <Formik
            initialValues={{
              numero: editingFactura.numero || '',
              titulo: editingFactura.titulo || '',
              fecha: editingFactura.fecha || format(new Date(), 'yyyy-MM-dd'),
              fechaServicio: editingFactura.fechaServicio || format(new Date(), 'yyyy-MM-dd'),
              estado: editingFactura.estado || 'pendiente',
              clienteNombreManual: editingFactura.clienteNombreManual || editingFactura.clienteNombre || '',
              subtotal: editingFactura.subtotal || 0,
              impuestoTotal: editingFactura.impuestoTotal || 0,
              total: editingFactura.total || 0,
              abono: editingFactura.abono || 0,
              saldoPendiente: editingFactura.saldoPendiente || 0,
              notas: editingFactura.notas || '',
              entregadoPor: editingFactura.entregadoPor || '',
              recibidoPor: editingFactura.recibidoPor || '',
              // Keep the original values for fields we don't edit
              clientId: editingFactura.clientId,
              lineas: editingFactura.lineas || [],
              manualInvoice: true,
            }}
            onSubmit={handleSubmitEditForm}
            validate={(values) => {
              const errors: Record<string, string> = {};
              
              if (!values.fecha) {
                errors.fecha = 'La fecha es requerida';
              }
              
              if (!values.clienteNombreManual) {
                errors.clienteNombreManual = 'El nombre del cliente es requerido';
              }
              
              if (values.abono > values.total) {
                errors.abono = 'El abono no puede ser mayor que el total';
              }
              
              // Require at least one line item
              if (!values.lineas || values.lineas.length === 0) {
                errors.lineas = 'Debe agregar al menos una línea a la factura';
              }
              
              // Update saldo pendiente
              values.saldoPendiente = values.total - values.abono;
              
              // Automatically set estado based on payment
              if (values.abono >= values.total) {
                values.estado = 'pagada';
              } else {
                values.estado = 'pendiente';
              }
              
              return errors;
            }}
          >
            {({ values, errors, touched, setFieldValue, handleSubmit, isSubmitting }) => (
              <Form className="space-y-6">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Factura #{values.numero}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Edite los campos importantes de esta factura manual
                  </p>
                </div>
                
                <div className="space-y-4">
                  {/* Main Details Section */}
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2">
                      Detalles importantes
                    </h3>
                    
                    <div className="flex flex-col gap-4">
                      {/* Fecha de emisión */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fecha de emisión
                        </label>
                        <Field
                          as={Input}
                          name="fecha"
                          type="date"
                          className="w-full dark:bg-gray-900 dark:text-white dark:border-gray-700"
                          onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                        />
                        {errors.fecha && touched.fecha && (
                          <div className="text-red-500 text-xs mt-1">{errors.fecha}</div>
                        )}
                      </div>
                      
                      {/* Estado (read-only) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Estado
                        </label>
                        <div className="mt-1 block w-full bg-gray-50 dark:bg-gray-700 rounded-md border border-gray-300 dark:border-gray-600 shadow-sm p-2 text-sm text-gray-700 dark:text-gray-300">
                          {values.estado === 'pagada' ? 'Pagada' : values.estado === 'anulada' ? 'Anulada' : 'Pendiente de pago'}
                        </div>
                      </div>
                      
                      {/* Cliente nombre manual */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Nombre del cliente
                        </label>
                        <Field
                          as={Input}
                          name="clienteNombreManual"
                          placeholder="Nombre del cliente"
                          type="text"
                          className="w-full dark:bg-gray-900 dark:text-white dark:border-gray-700"
                        />
                        {errors.clienteNombreManual && touched.clienteNombreManual && (
                          <div className="text-red-500 text-xs mt-1">{errors.clienteNombreManual}</div>
                        )}
                      </div>
                      
                      {/* Abono y saldo pendiente */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Abono realizado
                        </label>
                        <Field
                          as={Input}
                          name="abono"
                          placeholder="0.00"
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full dark:bg-gray-900 dark:text-white dark:border-gray-700"
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            const abono = parseFloat(e.target.value) || 0;
                            setFieldValue('abono', abono);
                            setFieldValue('saldoPendiente', values.total - abono);
                            
                            // Automatically set estado based on payment
                            if (abono >= values.total) {
                              setFieldValue('estado', 'pagada');
                            } else {
                              setFieldValue('estado', 'pendiente');
                            }
                          }}
                        />
                        {errors.abono && touched.abono && (
                          <div className="text-red-500 text-xs mt-1">{errors.abono}</div>
                        )}
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Saldo pendiente
                        </label>
                        <Input
                          value={values.saldoPendiente !== undefined ? values.saldoPendiente.toFixed(2) : '0.00'}
                          placeholder="0.00"
                          type="text"
                          className="w-full bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                          disabled
                        />
                      </div>
                      
                      {/* Notas */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Notas
                        </label>
                        <Field
                          as="textarea"
                          name="notas"
                          placeholder="Notas adicionales para esta factura"
                          className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring focus:ring-primary-500 focus:ring-opacity-50 dark:bg-gray-800 dark:text-white text-sm"
                          rows={3}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Líneas de factura (editable) */}
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-3 flex justify-between items-center">
                      <span>Líneas de factura</span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => {
                          // Add a new empty line
                          const newLineas = [...values.lineas, {
                            descripcion: '',
                            cantidad: 1,
                            precioUnitario: 0,
                            impuesto: 0,
                            subtotal: 0,
                            total: 0,
                          }];
                          setFieldValue('lineas', newLineas);
                        }}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" />
                        Añadir línea
                      </Button>
                    </h3>
                    
                    <div className="scrollbar-thin space-y-4 max-h-80 overflow-y-auto p-2">
                      {values.lineas && values.lineas.length > 0 ? (
                        values.lineas.map((linea: any, index: number) => (
                          <div key={index} className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 relative">
                            {/* Line header with delete button */}
                            <div className="flex justify-between items-center mb-3">
                              <h4 className="font-medium text-gray-900 dark:text-white text-sm">
                                Producto/servicio #{index + 1}
                              </h4>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className={`flex items-center gap-1.5 px-2 ${
                                  values.lineas.length === 1 
                                    ? "text-gray-400 dark:text-gray-500 cursor-not-allowed" 
                                    : "text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                                }`}
                                onClick={() => {
                                  if (values.lineas.length === 1) {
                                    // Show toast notification
                                    toast('La factura debe tener al menos una línea de items');
                                    return;
                                  }
                                  
                                  const updatedLineas = values.lineas.filter((_, i) => i !== index);
                                  setFieldValue('lineas', updatedLineas);
                                  
                                  // Recalculate totals
                                  const nuevoSubtotal = updatedLineas.reduce((sum, l) => sum + (l.subtotal || 0), 0);
                                  const nuevoImpuestoTotal = updatedLineas.reduce((sum, l) => sum + (l.impuesto || 0), 0);
                                  const nuevoTotal = updatedLineas.reduce((sum, l) => sum + (l.total || 0), 0);
                                  
                                  setFieldValue('subtotal', nuevoSubtotal);
                                  setFieldValue('impuestoTotal', nuevoImpuestoTotal);
                                  setFieldValue('total', nuevoTotal);
                                  setFieldValue('saldoPendiente', nuevoTotal - values.abono);
                                }}
                                aria-label="Eliminar línea"
                                disabled={values.lineas.length === 1}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                                <span className="text-xs">Eliminar</span>
                              </Button>
                            </div>
                            
                            {/* Line description */}
                            <div className="mb-3">
                              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Descripción
                              </label>
                              <input
                                type="text"
                                className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 dark:bg-gray-800 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                value={linea.descripcion}
                                onChange={(e) => {
                                  const updatedLineas = [...values.lineas];
                                  updatedLineas[index].descripcion = e.target.value;
                                  setFieldValue('lineas', updatedLineas);
                                }}
                                placeholder="Descripción del servicio"
                              />
                            </div>
                            
                            {/* Line details in grid */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Hotel/Alojamiento
                                </label>
                                <select
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 dark:bg-gray-800 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                  value={linea.hotel || ''}
                                  onChange={(e) => {
                                    const updatedLineas = [...values.lineas];
                                    updatedLineas[index].hotel = e.target.value;
                                    setFieldValue('lineas', updatedLineas);
                                  }}
                                >
                                  <option value="">Seleccionar hotel</option>
                                  {hotels.map((hotel, i) => (
                                    <option key={i} value={hotel}>
                                      {hotel}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Cantidad
                                </label>
                                <input
                                  type="number"
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 dark:bg-gray-800 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                  value={linea.cantidad}
                                  onChange={(e) => {
                                    const cantidad = parseFloat(e.target.value) || 0;
                                    const updatedLineas = [...values.lineas];
                                    updatedLineas[index].cantidad = cantidad;
                                    
                                    // Recalculate values
                                    const precioUnitario = updatedLineas[index].precioUnitario || 0;
                                    const subtotal = cantidad * precioUnitario;
                                    const impuestoRate = 0.18; // Default tax rate 18%
                                    const impuesto = subtotal * impuestoRate;
                                    const total = subtotal + impuesto;
                                    
                                    updatedLineas[index].subtotal = parseFloat(subtotal.toFixed(2));
                                    updatedLineas[index].impuesto = parseFloat(impuesto.toFixed(2));
                                    updatedLineas[index].total = parseFloat(total.toFixed(2));
                                    
                                    setFieldValue('lineas', updatedLineas);
                                    
                                    // Recalculate invoice totals
                                    const nuevoSubtotal = updatedLineas.reduce((sum, l) => sum + (l.subtotal || 0), 0);
                                    const nuevoImpuestoTotal = updatedLineas.reduce((sum, l) => sum + (l.impuesto || 0), 0);
                                    const nuevoTotal = updatedLineas.reduce((sum, l) => sum + (l.total || 0), 0);
                                    
                                    setFieldValue('subtotal', parseFloat(nuevoSubtotal.toFixed(2)));
                                    setFieldValue('impuestoTotal', parseFloat(nuevoImpuestoTotal.toFixed(2)));
                                    setFieldValue('total', parseFloat(nuevoTotal.toFixed(2)));
                                    setFieldValue('saldoPendiente', parseFloat((nuevoTotal - values.abono).toFixed(2)));
                                    
                                    // Update estado based on payment
                                    if (values.abono >= nuevoTotal) {
                                      setFieldValue('estado', 'pagada');
                                    } else {
                                      setFieldValue('estado', 'pendiente');
                                    }
                                  }}
                                  min="1"
                                  step="1"
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Precio unitario
                                </label>
                                <input
                                  type="number"
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 dark:bg-gray-800 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                  value={linea.precioUnitario !== undefined && linea.precioUnitario !== null ? linea.precioUnitario : ''}
                                  onChange={(e) => {
                                    const precioUnitario = parseFloat(e.target.value) || 0;
                                    const updatedLineas = [...values.lineas];
                                    updatedLineas[index].precioUnitario = precioUnitario;
                                    
                                    // Recalculate values
                                    const cantidad = updatedLineas[index].cantidad || 1;
                                    const subtotal = cantidad * precioUnitario;
                                    const impuestoRate = 0.18; // Default tax rate 18%
                                    const impuesto = subtotal * impuestoRate;
                                    const total = subtotal + impuesto;
                                    
                                    updatedLineas[index].subtotal = parseFloat(subtotal.toFixed(2));
                                    updatedLineas[index].impuesto = parseFloat(impuesto.toFixed(2));
                                    updatedLineas[index].total = parseFloat(total.toFixed(2));
                                    
                                    setFieldValue('lineas', updatedLineas);
                                    
                                    // Recalculate invoice totals
                                    const nuevoSubtotal = updatedLineas.reduce((sum, l) => sum + (l.subtotal || 0), 0);
                                    const nuevoImpuestoTotal = updatedLineas.reduce((sum, l) => sum + (l.impuesto || 0), 0);
                                    const nuevoTotal = updatedLineas.reduce((sum, l) => sum + (l.total || 0), 0);
                                    
                                    setFieldValue('subtotal', parseFloat(nuevoSubtotal.toFixed(2)));
                                    setFieldValue('impuestoTotal', parseFloat(nuevoImpuestoTotal.toFixed(2)));
                                    setFieldValue('total', parseFloat(nuevoTotal.toFixed(2)));
                                    setFieldValue('saldoPendiente', parseFloat((nuevoTotal - values.abono).toFixed(2)));
                                    
                                    // Update estado based on payment
                                    if (values.abono >= nuevoTotal) {
                                      setFieldValue('estado', 'pagada');
                                    } else {
                                      setFieldValue('estado', 'pendiente');
                                    }
                                  }}
                                  min="0"
                                  step="0.01"
                                />
                              </div>
                            </div>

                            {/* Secondary details row */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Subtotal
                                </label>
                                <input
                                  type="text"
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                  value={linea.subtotal !== undefined && linea.subtotal !== null ? `$${linea.subtotal.toFixed(2)}` : '$0.00'}
                                  readOnly
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Impuesto
                                </label>
                                <input
                                  type="text"
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                  value={linea.impuesto !== undefined && linea.impuesto !== null ? `$${linea.impuesto.toFixed(2)}` : '$0.00'}
                                  readOnly
                                />
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Total
                                </label>
                                <input
                                  type="text"
                                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 font-medium bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                  value={linea.total !== undefined && linea.total !== null ? `$${linea.total.toFixed(2)}` : '$0.00'}
                                  readOnly
                                />
                              </div>
                            </div>

                            {/* Final row with total */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                              <div>
                                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                  Fecha de servicio
                                </label>
                                <Field
                                  as={Input}
                                  type="date"
                                  className="w-full dark:bg-gray-900 dark:text-white dark:border-gray-700"
                                  value={linea.fechaServicio ? format(new Date(linea.fechaServicio), 'yyyy-MM-dd') : ''}
                                  onFocus={(e: React.FocusEvent<HTMLInputElement>) => e.target.showPicker()}
                                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const updatedLineas = [...values.lineas];
                                    updatedLineas[index].fechaServicio = e.target.value ? new Date(e.target.value).toISOString() : null;
                                    setFieldValue('lineas', updatedLineas);
                                  }}
                                  placeholder="Seleccionar fecha de servicio"
                                />
                              </div>
                              <div className="md:col-span-2">
                              </div>
                            </div>

                            {/* Optional fields in accordion */}
                            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                              <details className="text-sm">
                                <summary className="text-primary-600 dark:text-gray-200 cursor-pointer font-medium">
                                  Campos adicionales
                                </summary>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                      Tipo de servicio
                                    </label>
                                    <input
                                      type="text"
                                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 dark:bg-gray-800 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                      value={linea.tipoServicio || ''}
                                      onChange={(e) => {
                                        const updatedLineas = [...values.lineas];
                                        updatedLineas[index].tipoServicio = e.target.value;
                                        setFieldValue('lineas', updatedLineas);
                                      }}
                                      placeholder="Ingrese tipo de servicio"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                      Destino
                                    </label>
                                    <input
                                      type="text"
                                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 dark:bg-gray-800 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                      value={linea.destino || ''}
                                      onChange={(e) => {
                                        const updatedLineas = [...values.lineas];
                                        updatedLineas[index].destino = e.target.value;
                                        setFieldValue('lineas', updatedLineas);
                                      }}
                                      placeholder="Destino turístico"
                                    />
                                  </div>
                                  
                                  <div>
                                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                                      Costo proveedor
                                    </label>
                                    <input
                                      type="text"
                                      className="w-full border border-gray-300 dark:border-gray-600 rounded-md text-sm py-1.5 px-3 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                      value={linea.costoProveedor !== undefined && linea.costoProveedor !== null ? 
                                        (typeof linea.costoProveedor === 'number' ? 
                                          linea.costoProveedor.toFixed(2) : '0.00') : '0.00'}
                                      readOnly
                                      disabled
                                      placeholder="Auto-calculado"
                                    />
                                  </div>
                                </div>
                              </details>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="text-sm text-gray-500 dark:text-gray-400 italic text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-dashed border-gray-300 dark:border-gray-700">
                          No hay líneas en esta factura. Haga clic en "Añadir línea" para comenzar.
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Importes Section */}
                  <div className="p-4 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700 pb-2 mb-4">
                      Importes <span className="text-xs text-gray-500 dark:text-gray-400">(solo lectura)</span>
                    </h3>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Subtotal:</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">${values.subtotal !== undefined && values.subtotal !== null ? values.subtotal.toFixed(2) : '0.00'}</span>
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Impuestos:</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">${values.impuestoTotal !== undefined && values.impuestoTotal !== null ? values.impuestoTotal.toFixed(2) : '0.00'}</span>
                      </div>
                      
                      <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                        <span className="text-base font-bold text-gray-800 dark:text-white">Total:</span>
                        <span className="text-base font-bold text-blue-600 dark:text-blue-400">${values.total !== undefined && values.total !== null ? values.total.toFixed(2) : '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700 gap-3">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="px-4 py-2" 
                    onClick={handleCloseEditModal}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    type="submit" 
                    variant="primary" 
                    className="px-4 py-2" 
                    disabled={isSubmitting || !values.lineas || values.lineas.length === 0}
                  >
                    {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </Modal>
      )}

      {/* Modal for viewing invoice details */}
      {selectedFactura && isDetailsModalOpen && (
        <Modal
          isOpen={isDetailsModalOpen}
          onClose={handleCloseDetailsModal}
          title={`Factura #${selectedFactura.numero}`}
          size="lg"
        >
          {/* Documento de factura */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm p-6 mx-auto max-w-3xl">
            {/* Encabezado de factura */}
            <div className="border-b border-gray-200 dark:border-gray-700 pb-6 mb-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    <FileText className="text-blue-500 h-6 w-6" />
                    FACTURA
                  </h1>
                  <p className="text-xl font-semibold text-blue-600 dark:text-blue-400 mt-1">#{selectedFactura.numero}</p>
                </div>
                <div className="flex flex-col items-end">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Fecha:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{format(parseISO(selectedFactura.fecha), 'dd/MM/yyyy')}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Estado:</span>
                    <span className={`text-sm font-medium px-2.5 py-1 rounded-full text-xs font-medium ${
                      selectedFactura.estado === 'pagada' ? 'bg-green-100 text-green-700 border border-green-200 dark:bg-green-900/40 dark:text-green-400 dark:border-green-800' :
                      selectedFactura.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/40 dark:text-yellow-400 dark:border-yellow-800' :
                      'bg-red-100 text-red-700 border border-red-200 dark:bg-red-900/40 dark:text-red-400 dark:border-red-800'
                    }`}>
                      {selectedFactura.estado === 'pagada' ? 'Pagada' : 
                       selectedFactura.estado === 'pendiente' ? 'Pendiente' : 'Anulada'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Información del cliente y facturación */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-2">Cliente</h3>
                <div className="mb-2">
                  <span className="block text-base font-medium text-gray-900 dark:text-white">{selectedFactura.clienteNombre}</span>
                </div>
                {selectedFactura.clientType === 'empresa' ? (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                    Empresa (B2B)
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                    Individual (B2C)
                  </span>
                )}
              </div>
                
              <div className="border-l-4 border-gray-300 pl-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-2">Detalles de Facturación</h3>
                <div className="grid grid-cols-1 gap-2">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2">Factura #:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{selectedFactura.numero}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400 mr-2">Fecha de emisión:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{format(parseISO(selectedFactura.fecha), 'dd/MM/yyyy')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Items Table */}
            <div className="mb-8">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">Elementos Facturados</h3>
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800/50">
                    <tr>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Descripción</th>
                      <th scope="col" className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Cantidad</th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Precio</th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Impuesto</th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Total</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
                    {selectedFactura.lineas && selectedFactura.lineas.map((linea, index) => (
                      <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">{linea.descripcion}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-center">{linea.cantidad}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">${linea.precioUnitario !== undefined && linea.precioUnitario !== null ? linea.precioUnitario.toFixed(2) : '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 text-right">${linea.impuesto !== undefined && linea.impuesto !== null ? linea.impuesto.toFixed(2) : '0.00'}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300 text-right font-medium">${linea.total !== undefined && linea.total !== null ? linea.total.toFixed(2) : '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Add Notes Section after Items Table */}
            {facturaNotas && facturaNotas.length > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">Notas de Crédito y Débito</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-800/50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Tipo</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Número</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Fecha</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Motivo</th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Monto</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700">
                      {facturaNotas.map((nota, index) => (
                        <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              nota.tipo === 'credito' 
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                              {nota.tipo === 'credito' ? 'Crédito' : 'Débito'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-300">{nota.numero}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{format(new Date(nota.fecha), 'dd/MM/yyyy')}</td>
                          <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 capitalize">{nota.motivo}</td>
                          <td className={`px-4 py-3 text-sm font-medium text-right ${
                            nota.tipo === 'credito' 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {nota.tipo === 'credito' ? '-' : '+'}${nota.monto.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {/* Financial Summary - Update to include notas */}
            <div className="flex justify-end mb-8">
              <div className="w-full md:w-64">
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  {/* Show discount if present */}
                  {selectedFactura.reservation && selectedFactura.reservation.descuento > 0 && (
                    <>
                      <div className="flex justify-between py-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Subtotal (antes de descuento):</span>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          ${((selectedFactura.subtotal / (1 - (selectedFactura.reservation.descuento / 100)))).toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          Descuento ({selectedFactura.reservation.descuento}%):
                        </span>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">
                          -${((selectedFactura.subtotal / (1 - (selectedFactura.reservation.descuento / 100))) * (selectedFactura.reservation.descuento / 100)).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between py-1">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Subtotal:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">${selectedFactura.subtotal !== undefined && selectedFactura.subtotal !== null ? selectedFactura.subtotal.toFixed(2) : '0.00'}</span>
                  </div>
                  <div className="flex justify-between py-1">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Impuestos:</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">${selectedFactura.impuestoTotal !== undefined && selectedFactura.impuestoTotal !== null ? selectedFactura.impuestoTotal.toFixed(2) : '0.00'}</span>
                  </div>
                  
                  {/* Add notas adjustments */}
                  {facturaNotas && facturaNotas.length > 0 && (() => {
                    let totalNotasAdjustment = 0;
                    
                    // Calculate total adjustment from all notas
                    facturaNotas.forEach(nota => {
                      if (nota.tipo === 'credito') {
                        totalNotasAdjustment -= nota.monto;
                      } else {
                        totalNotasAdjustment += nota.monto;
                      }
                    });
                    
                    // Return adjustment row if there's any adjustment
                    if (totalNotasAdjustment !== 0) {
                      const label = totalNotasAdjustment < 0 ? "Ajuste (notas de crédito):" : "Ajuste (notas de débito):";
                      return (
                        <div className="flex justify-between py-1">
                          <span className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</span>
                          <span className={`text-sm font-medium ${
                            totalNotasAdjustment < 0 
                              ? 'text-red-600 dark:text-red-400' 
                              : 'text-green-600 dark:text-green-400'
                          }`}>
                            {totalNotasAdjustment < 0 ? '-' : '+'}${Math.abs(totalNotasAdjustment).toFixed(2)}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}
                  
                  <div className="flex justify-between py-2 mt-1 border-t border-gray-200 dark:border-gray-700">
                    <span className="text-base font-bold text-gray-800 dark:text-white">Total:</span>
                    <span className="text-base font-bold text-blue-600 dark:text-blue-400">${selectedFactura.total !== undefined && selectedFactura.total !== null ? selectedFactura.total.toFixed(2) : '0.00'}</span>
                  </div>
                  
                  {/* Existing code for pending invoices */}
                  {selectedFactura.estado === 'pendiente' && (
                    <>
                      <div className="flex justify-between py-1 mt-3">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Abono:</span>
                        <span className="text-sm font-medium text-green-600 dark:text-green-400">${selectedFactura.abono !== undefined && selectedFactura.abono !== null ? selectedFactura.abono.toFixed(2) : '0.00'}</span>
                      </div>
                      <div className="flex justify-between py-1">
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Saldo pendiente:</span>
                        <span className="text-sm font-medium text-red-600 dark:text-red-400">${selectedFactura.saldoPendiente !== undefined && selectedFactura.saldoPendiente !== null ? selectedFactura.saldoPendiente.toFixed(2) : '0.00'}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Notes */}
            {selectedFactura.notas && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">Notas</h3>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 text-sm text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700">
                  {selectedFactura.notas}
                </div>
              </div>
            )}

            {/* Comisión de Agencia - Mostramos si existe y proviene de una reserva B2B */}
            {selectedFactura.reservation && 
             selectedFactura.reservation.type === 'b2b' && 
             'agenciaComision' in selectedFactura.reservation && 
             typeof selectedFactura.reservation.agenciaComision === 'number' && 
             selectedFactura.reservation.agenciaComision > 0 && (
              <div className="mb-8">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase mb-3 border-b border-gray-200 dark:border-gray-700 pb-2">Comisión de Agencia</h3>
                <div className="bg-blue-50 dark:bg-blue-900/10 rounded-lg p-4 text-sm border border-blue-100 dark:border-blue-800">
                  <p className="font-medium text-blue-700 dark:text-blue-400">
                    La agencia recibe una comisión de ${selectedFactura.reservation.agenciaComision.toFixed(2)}
                  </p>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">
                    Esta comisión está incluida en el precio total facturado al cliente final
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-12 border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex justify-between items-center">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  <p>* Esta factura fue generada automáticamente.</p>
                  <p>* Por favor, conserve este documento para su referencia.</p>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleCloseDetailsModal}
                  >
                    Cerrar
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => selectedFactura.id && handleExportFactura(selectedFactura.id)}
                    startIcon={<Download className="w-4 h-4" />}
                  >
                    Exportar PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal para confirmar eliminación */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setFacturaIdToDelete(null);
        }}
        title="Confirmación de eliminación"
        size="md"
      >
        <div className="space-y-6 px-2">
          <div className="space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              ¿Está seguro de eliminar esta factura?
            </p>
            
            <div className="space-y-3">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción eliminará permanentemente todos los datos asociados a la factura, incluyendo:
              </p>
              
              <ul className="list-disc list-inside text-sm text-gray-700 dark:text-gray-300 ml-4 space-y-1">
                <li>Detalles de la factura y líneas de items</li>
                <li>Registro de pagos y transacciones</li>
                <li>Historial de cambios y notas</li>
                <li>Documentos y archivos relacionados</li>
              </ul>
            </div>

            <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-lg">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Esta acción no se puede deshacer.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setFacturaIdToDelete(null);
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={async () => {
                if (facturaIdToDelete !== null) {
                  try {
                    await api.deleteFactura(facturaIdToDelete);
                    toast.success('Factura eliminada exitosamente');
                    loadFacturas(); // Reload the list
                    setIsDeleteModalOpen(false);
                    setFacturaIdToDelete(null);
                    
                    // Para compatibilidad con la implementación anterior
                    if (onFacturaDeleted) {
                      await onFacturaDeleted();
                    }
                  } catch (error) {
                    toast.error('Error al eliminar la factura');
                  }
                }
              }}
            >
              Sí, eliminar factura
            </Button>
          </div>
        </div>
      </Modal>
    </ComponentCard>
  );
};

export default FacturaList;